/**
 * Per-agent human-handover config: where the alert goes, and whether the
 * escalation stops the AI.
 *
 * When the AI invokes `human_handover`, the tenant is alerted through (a) the
 * handover-local CRM action and (b) the general Notification Channels subsystem
 * (`lib/notifications/`): email / Telegram / … instances the tenant defined once
 * and selected here by id. This module is the single source of truth for the
 * per-agent CONFIG shape + the effective-config resolver. It is intentionally
 * dependency-free (pure types + logic) so agent config types and the tool can
 * import it without cycles.
 *
 * Storage: per-agent at `<agent>.config.human_handover`.
 * - `notification_channel_ids` references tenant `notification_channels`
 *   instances, resolved tenant-scoped at runtime via the notifications DAL — a
 *   config id is never proof of ownership.
 * - CRM enablement + routing are NOT here — they stay single-sourced in
 *   `crm_bindings.config.handover` (`CrmService.handleHandover` self-gates on
 *   `handover.enabled`), so the agent config never holds a competing CRM toggle.
 * - The master "is the tool offered to the LLM" toggle is separate (email:
 *   `human_handover.enabled`; message: `tools_enabled.human_handover`).
 */

/**
 * Whether an escalation stops the AI. Message agents only — every other scope is
 * forced to `always` by `resolveHandoverPauseDecision`.
 */
export type HandoverPauseMode = 'always' | 'never' | 'ai_decides'

export const HANDOVER_PAUSE_MODES: readonly HandoverPauseMode[] = [
  'always',
  'never',
  'ai_decides',
] as const

/** Absent `pause_mode` keeps the behaviour every existing agent already has. */
export const DEFAULT_HANDOVER_PAUSE_MODE: HandoverPauseMode = 'always'

/**
 * Per-agent handover config, stored at `<agent>.config.human_handover`.
 */
export interface HandoverConfig {
  /**
   * Ids of tenant notification channel instances (email/telegram/…) that receive
   * the handover alert. Empty/absent = no external notification (the CRM action
   * is independent). Resolved tenant-scoped via the notifications DAL.
   */
  notification_channel_ids?: string[]
  /**
   * `always` — escalating pauses the AI (historical behaviour, and the default).
   * `never` — alert the team and keep answering the customer.
   * `ai_decides` — the model chooses per escalation via the `pause_ai` argument.
   */
  pause_mode?: HandoverPauseMode
}

/** Fully-resolved handover settings — what the tool reads. */
export interface ResolvedHandoverSettings {
  notificationChannelIds: string[]
  pauseMode: HandoverPauseMode
}

export interface ResolveHandoverSettingsInput {
  /** `<agent>.config.human_handover` if present. */
  config?: HandoverConfig | null
}

/**
 * Resolve the effective handover settings for an agent. CRM enablement is
 * intentionally absent — it lives in `crm_bindings.config.handover` and is
 * enforced by the CRM service itself.
 */
export function resolveHandoverSettings(
  input: ResolveHandoverSettingsInput,
): ResolvedHandoverSettings {
  return {
    notificationChannelIds: input.config?.notification_channel_ids ?? [],
    pauseMode: input.config?.pause_mode ?? DEFAULT_HANDOVER_PAUSE_MODE,
  }
}

/**
 * Authoritative normalization for the handover section — the reply path reads it
 * on every escalation, so a malformed value must never be stored. `null` = reject
 * the request.
 *
 * Absence is preserved: a key missing from the input stays missing in the output.
 * Materializing defaults here would turn a partial PATCH into a wipe of the
 * sibling field, since the config route merges one level deep.
 *
 * Message scope only. The email block is `HandoverConfig & { enabled?: boolean }`,
 * and unknown keys are dropped here — teach this about `enabled` before wiring it
 * into the email route, or the master tool toggle is stripped on every PATCH.
 */
export function normalizeHandoverConfig(value: unknown): HandoverConfig | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null

  const { notification_channel_ids: ids, pause_mode: pauseMode } = value as HandoverConfig

  if (ids !== undefined && (!Array.isArray(ids) || ids.some((id) => typeof id !== 'string'))) {
    return null
  }
  if (pauseMode !== undefined && !HANDOVER_PAUSE_MODES.includes(pauseMode)) return null

  return {
    ...(ids !== undefined ? { notification_channel_ids: ids.map((id) => id.trim()).filter(Boolean) } : {}),
    ...(pauseMode !== undefined ? { pause_mode: pauseMode } : {}),
  }
}
